import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ContentWidth = 'default' | 'full';

export interface LayoutWidthContextValue {
  contentWidth: ContentWidth;
  setContentWidth: (width: ContentWidth) => void;
}

const LayoutWidthContext = createContext<LayoutWidthContextValue | null>(null);

/**
 * Provides the centralized content-width state for the app shell.
 * Mounted high in `Layout` so both `<main>` and the routed pages can read/write it.
 */
export function LayoutWidthProvider({ children }: { children: ReactNode }) {
  const [contentWidth, setContentWidth] = useState<ContentWidth>('default');

  const value = useMemo<LayoutWidthContextValue>(
    () => ({ contentWidth, setContentWidth }),
    [contentWidth],
  );

  return (
    <LayoutWidthContext.Provider value={value}>
      {children}
    </LayoutWidthContext.Provider>
  );
}

/**
 * Accessor for the Layout container — reads the current content width and setter.
 */
export function useLayoutWidth(): LayoutWidthContextValue {
  const ctx = useContext(LayoutWidthContext);
  if (!ctx) {
    throw new Error('useLayoutWidth must be used within a LayoutWidthProvider');
  }
  return ctx;
}

/**
 * Opt a page out of the default `max-w-7xl` container so it spans the full
 * content width (graph maps, wide multi-column tables). Sets `'full'` on mount
 * and restores `'default'` on unmount, so navigating away resets automatically.
 */
export function useFullBleed(): void {
  const { setContentWidth } = useLayoutWidth();

  // `setContentWidth` is stable (state setter), so this runs once on mount.
  const apply = useCallback(() => {
    setContentWidth('full');
    return () => setContentWidth('default');
  }, [setContentWidth]);

  useEffect(apply, [apply]);
}
